import { createStore } from "@thekrystalship/krystal/lib/store";
import { assistantBase } from "./config.js";

// assistantSession.js — where the assistant is, and the session it is spoken to with.
//
// The assistant signs nobody in. It checks the panel's own session token, so the session a browser
// presents to it is the one it already holds for the cluster: there is no session with the assistant,
// only the surface's own, handed in, and the base the build names (`VITE_ASSISTANT`).
//
// The session is installed by the panel, which holds it in its own store; resolving it here would
// mean importing that store into this module. `fresh` is the whole contract the assistant's client
// asks of it.
//
// Nothing installed, or no base configured ⇒ no route and no session, which is the honest answer
// before the panel has said.

// The session: `statusOf`, `bearer(options)` (a token, renewed on `{ force: true }`) and `subscribe`, so
// a component re-renders when the status changes. The panel's sessionStore speaks it.
let credential = null;
let unsubscribe = null;

// The status, mirrored so a component can subscribe to this store rather than to whichever
// session the surface happened to install.
const store = createStore({ status: "none" });
function sync() {
  const status = credential ? credential.statusOf() : "none";
  if (store.getState().status !== status) store.setState({ status });
}
function setCredential(c) {
  if (unsubscribe) { unsubscribe(); unsubscribe = null; }
  credential = c || null;
  if (credential && typeof credential.subscribe === "function") unsubscribe = credential.subscribe(sync);
  sync();
}

// The assistant's base ("/assistant", or an origin). Null is an honest "no route" — never guessed from
// the panel's own origin, which would send a turn to whatever happened to serve the bundle.
function baseOf() {
  const base = assistantBase();
  return base || null;
}
const hasRoute = () => !!baseOf();

const routed = () => !!(credential && hasRoute());
const statusOf = () => (routed() ? credential.statusOf() : "none");

// A token for the assistant's client; rejects with nothing installed, which the client reads as signed out.
const fresh = (options) => (routed()
  ? credential.bearer(options)
  : Promise.reject(Object.assign(new Error("There is no session."), { code: "none" })));

const assistantSession = Object.assign(store, { setCredential, hasRoute, baseOf, statusOf, fresh });

export { assistantSession };
